import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Render `children` where they normally go, or inside `to` when it is
 * given -- WITHOUT remounting them.
 *
 * The reading room's full screen docks the sitting's own controls beside
 * the images: the candidate's camera and recorder. Rendering a second
 * copy there would restart the camera and, worse, the RECORDING -- a
 * screening round is one continuous recording with no retake. So the
 * children stay where they are in the React tree and only their DOM host
 * moves: one stable element, appended to the in-place anchor or to the
 * dock.
 *
 * Both the anchor and the host are `display: contents`, so the children
 * lay out as direct children of whatever holds them -- the page's flex
 * row, or the dock's column -- exactly as before.
 *
 * A <video> PAUSES when it leaves the document (the HTML spec's removal
 * steps), and moving an element is a removal and an insertion; a live
 * camera preview would freeze on its last frame. Any video that has a
 * stream is told to play again after each move.
 */
export function Movable({ to, children }: { to: HTMLElement | null; children: ReactNode }) {
  const [host] = useState(() => {
    const el = document.createElement("div");
    el.style.display = "contents";
    return el;
  });
  const anchor = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const target = to ?? anchor.current;
    if (!target || host.parentElement === target) return;
    target.appendChild(host);
    for (const video of host.querySelectorAll("video")) {
      if (video.srcObject) void video.play().catch(() => undefined);
    }
  }, [to, host]);

  useLayoutEffect(() => () => host.remove(), [host]);

  return (
    <>
      <div ref={anchor} style={{ display: "contents" }} />
      {createPortal(children, host)}
    </>
  );
}
